import { COOKIE_CONSENT_DISALLOWED, LocalStorageCapsule, SetOptions } from 'data-capsule';
import { useCallback, useEffect, useState } from 'react';

export const capsule = LocalStorageCapsule({ namespace: 'MMGR6' });

interface IUseDataCapsule<T> {
  value: T;
  set: (value: T) => Promise<void>;
  isLoading: boolean;
}

export const useDataCapsule = <T>(
  key,
  initialValue: T,
  fallbackValue: T = initialValue,
  options: SetOptions = {},
): IUseDataCapsule<T> => {
  const [{ localState, isLoading }, _updateLocalState] = useState<{ localState: T; isLoading: boolean }>({
    localState: initialValue,
    isLoading: true,
  });

  const updateLocalState = useCallback((value) => _updateLocalState({ localState: value, isLoading: false }), []);

  const updateLocalStateAndCapsule = useCallback(
    async (value) => {
      const { expiration = Number.MAX_SAFE_INTEGER, category = 'functional' } = options;

      try {
        await capsule.setItem(key, value, { expiration, category });
        updateLocalState(value);
        return value;
      } catch (error) {
        updateLocalState(fallbackValue);
        return fallbackValue;
      }
    },
    [key],
  );

  useEffect(() => {
    if (!key) {
      return;
    }

    capsule.getItem(key).then(updateLocalState, (error) => {
      if (error === COOKIE_CONSENT_DISALLOWED) {
        return updateLocalState(fallbackValue);
      }

      return updateLocalStateAndCapsule(initialValue);
    });
  }, [key]);

  return {
    value: localState,
    isLoading,
    set: updateLocalStateAndCapsule,
  };
};
